import axios from 'axios';
import { API_URL } from '../config/api';
import { readTemplatesFromRealm, upsertRealmDocs, realmAvailable } from '../realm';

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const TEMPLATE_TYPES = [
  { value: 'MESSAGE', label: 'Message' },
  { value: 'APPOINTMENT', label: 'Appointment' },
  { value: 'ANNOUNCEMENT', label: 'Announcement' },
  { value: 'WORKFLOW', label: 'Workflow' },
  { value: 'TASK', label: 'Task' },
  { value: 'GENERAL', label: 'General' },
  { value: 'SEND_USING_API', label: 'Send using API' },
];

/** Placeholders like {name} in Send using API rich text. */
export const extractBraceParams = (html) => {
  if (!html) return [];
  let text = String(html);
  if (typeof document !== 'undefined') {
    const el = document.createElement('div');
    el.innerHTML = html;
    text = el.textContent || '';
  } else {
    text = text.replace(/<[^>]+>/g, ' ');
  }
  const params = [];
  const seen = new Set();
  const re = /\{([^{}]+)\}/g;
  let match;
  while ((match = re.exec(text))) {
    const name = String(match[1] || '').replace(/\u00a0/g, ' ').trim().replace(/\s+/g, ' ');
    if (!name || name.length > 64) continue;
    if (!/^[A-Za-z_][A-Za-z0-9_\- ]*$/.test(name)) continue;
    const key = name.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    params.push(name);
  }
  return params;
};

const htmlToPlainText = (html) => {
  if (!html) return '';
  if (typeof document !== 'undefined') {
    const el = document.createElement('div');
    el.innerHTML = html;
    return String(el.textContent || '').replace(/\u00a0/g, ' ').trim();
  }
  return String(html).replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
};

export const hasApiTemplateBody = (html) => htmlToPlainText(html).length > 0
  || /<img\b/i.test(String(html || ''))
  || /href\s*=/i.test(String(html || ''));

export const FIELD_TYPES = [
  { value: 'TEXT', label: 'Text' },
  { value: 'TEXTAREA', label: 'Long text' },
  { value: 'NUMBER', label: 'Number' },
  { value: 'DATE', label: 'Date' },
  { value: 'SELECT', label: 'Select' },
  { value: 'USER', label: 'User' },
];

export const ASSIGNEE_MODES = [
  { value: 'SENDER', label: 'Only Sender' },
  { value: 'NONE', label: 'No one' },
  { value: 'ALL_RECEIVERS', label: 'All Receivers' },
  { value: 'CUSTOM', label: 'Custom Select' },
];

export const POST_ACTIONS = [
  { value: 'TRACKING', label: 'Tracking' },
  { value: 'ACKNOWLEDGE', label: 'Acknowledge' },
];

/** Who can see/use the template. Only admins may choose PUBLIC. */
export const SCOPE_OPTIONS = [
  { value: 'USER', label: 'Only me' },
  { value: 'PUBLIC', label: 'Everyone (Public)' },
];

export const VISIBILITY_OPTIONS = [
  { value: 'PUBLIC', label: 'Public' },
  { value: 'DIRECT_ONLY', label: 'Only in Direct Chat' },
  { value: 'CHANNEL_ONLY', label: 'Only in Channel' },
  { value: 'CUSTOM', label: 'Custom setting' },
];

export const fetchMessageTemplates = async (includeInactive = false) => {
  if (!includeInactive && (await realmAvailable())) {
    const cached = await readTemplatesFromRealm();
    return cached || [];
  }

  const response = await axios.get(`${API_URL}/message-templates`, {
    headers: authHeaders(),
    params: { includeInactive },
  });
  upsertRealmDocs('MessageTemplate', response.data || []);
  return response.data || [];
};

export const fetchTemplatesForChannel = async (channelId) => {
  if (await realmAvailable()) {
    const cached = await readTemplatesFromRealm();
    return cached || [];
  }

  const response = await axios.get(`${API_URL}/message-templates/for-channel/${channelId}`, {
    headers: authHeaders(),
  });
  upsertRealmDocs('MessageTemplate', response.data || []);
  return response.data || [];
};

export const createMessageTemplate = async (payload) => {
  const response = await axios.post(`${API_URL}/message-templates`, payload, {
    headers: authHeaders(),
  });
  return response.data;
};

export const updateMessageTemplate = async (id, payload) => {
  const response = await axios.put(`${API_URL}/message-templates/${id}`, payload, {
    headers: authHeaders(),
  });
  return response.data;
};

export const generateTemplateApiKey = async (id) => {
  const response = await axios.post(`${API_URL}/message-templates/${id}/api-key`, {}, {
    headers: authHeaders(),
  });
  return response.data;
};

export const setTemplateApiKeyActive = async (id, active) => {
  const response = await axios.put(`${API_URL}/message-templates/${id}/api-key`, { active }, {
    headers: authHeaders(),
  });
  return response.data;
};

export const deleteMessageTemplate = async (id) => {
  const response = await axios.delete(`${API_URL}/message-templates/${id}`, {
    headers: authHeaders(),
  });
  return response.data;
};

export const postMessageTemplate = async (payload) => {
  const response = await axios.post(`${API_URL}/message-templates/post`, payload, {
    headers: authHeaders(),
  });
  return response.data;
};

export const acknowledgeTemplateMessage = async (messageId) => {
  const response = await axios.post(
    `${API_URL}/message-templates/messages/${messageId}/acknowledge`,
    {},
    { headers: authHeaders() }
  );
  return response.data;
};

export const fetchPendingTemplateAnnouncements = async () => {
  const response = await axios.get(`${API_URL}/message-templates/announcements/pending`, {
    headers: authHeaders(),
  });
  return response.data || [];
};

export default {
  fetchMessageTemplates,
  fetchTemplatesForChannel,
  createMessageTemplate,
  updateMessageTemplate,
  deleteMessageTemplate,
  postMessageTemplate,
  acknowledgeTemplateMessage,
  fetchPendingTemplateAnnouncements,
  generateTemplateApiKey,
  setTemplateApiKeyActive,
};
